import React, { useEffect, useState, memo } from 'react';
import Creatable from 'react-select/creatable';
import { Text, Button } from '@cuemath/web-comps';
import { Separator, Spinner, TextInput } from '@cuemath/comps';

import { useFunnelExperiment, useFunnelExperimentList } from '../api/get-funnel';
import ChipGroup from '../components/chips';
import CountrySelect from '../components/country-select';
import RangePicker from '../components/range-picker';
import MultiSelectApply from '../components/multi-select-apply';
import FunnelTable, { buildContributorColumns } from '../components/funnel-table';
import FunnelBarChart from '../components/charts/funnel-bar-chart';
import {
  Card,
  SectionLabel,
  ChartBox,
  StateBlock,
  FiltersBar,
  FilterField,
  ActionsField,
  ChipGroupsRow,
  FieldLabelText,
  LoadingOverlay,
  UI,
} from '../styled-comps';
import {
  METRICS,
  METRIC_KEYS,
  METRIC_LABELS,
  METRIC_COLORS,
  REGION_OPTIONS,
  USER_TYPE_OPTIONS,
  ANALYSIS_MIN_DATE,
} from '../constants';
import { asRows, metricFields, buildRangeParams } from '../helpers';
import { useSettledKey } from '../hooks/use-settled-key';

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Default to the last ~3 calendar months — experiments run in bounded windows,
// and it keeps the (each ~5-8s) Redshift query fast by default.
const defaultStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() - 2, 1);
};

const defaultDraft = () => ({
  start: defaultStart(),
  end: today(),
  experiment: '',
  page: '',
  regions: [],
  countries: [],
  userTypes: [],
});

// `page` (optional) scopes every variant's funnel to leads on that page — the
// lead counts when the page matches EITHER its signup page OR its signup-session
// landing page (backend `analyze_path`, same rule as the Page Analysis tab).
const buildQuery = applied =>
  buildRangeParams(applied.start, applied.end, {
    regions: applied.regions,
    countries: applied.countries,
    userTypes: applied.userTypes,
    experiment: applied.experiment,
    analyzePath: applied.page.trim(),
  });

// Experiment Analysis tab (same shape as Page Analysis): date range + experiment
// name + region + user type -> one funnel row per variant of that experiment
// over the whole date range. The backend /experiment/ grouping already returns
// one row per variant value ('a', 'b', ...), so a 3-month homepageImprovements
// window returns every variant seen in it. A frontend variant multi-select (with
// Apply) then narrows the table + comparison chart to just the variants the user
// wants to compare — no refetch.
const ExperimentAnalysis = memo(() => {
  const [draft, setDraft] = useState(defaultDraft);
  const [applied, setApplied] = useState(defaultDraft);
  // Client-side variant filter (which variant values the table/chart show).
  const [selected, setSelected] = useState([]);

  const { data, get, isProcessing, error } = useFunnelExperiment();

  // Ongoing experiments (from recent website signups) — the dropdown suggestions.
  // Fetched once on mount; a failure just leaves the dropdown empty (free-text
  // still works). No deps: the rest-api `get` is not referentially stable.
  const { data: expListData, get: getExpList } = useFunnelExperimentList();
  useEffect(() => {
    getExpList('wm-experiment-list');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const experimentOptions = (Array.isArray(expListData) ? expListData : []).map(name => ({
    label: name,
    value: name,
  }));

  const hasExperiment = !!applied.experiment.trim();
  const pageScope = applied.page.trim();
  // Heading suffix so the reader knows the numbers are page-scoped.
  const scopeSuffix = pageScope ? ` on "${pageScope}"` : '';
  const appliedQuery = hasExperiment ? buildQuery(applied) : null;
  const queryKey = appliedQuery ? JSON.stringify(appliedQuery) : '';

  useEffect(() => {
    if (appliedQuery) get('wm-experiment-analysis', appliedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  // True only once `data` reflects the applied query — so the variant label,
  // table and chart never render the previous experiment's rows for the render
  // between Apply and the store swapping in the new query's (empty) resource.
  const fresh = useSettledKey(queryKey, isProcessing, error);

  // Every variant value returned, sorted (a, b, ..., l) — drives the dropdown.
  const allVariants = (fresh ? asRows(data) : [])
    .map(row => row.variant)
    .filter(variant => variant !== null && variant !== undefined && variant !== '')
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const variantsKey = allVariants.join('|');

  // Reset the client-side selection to "all variants" whenever the set of
  // returned variants changes (keyed on variantsKey) — e.g. a new experiment, or
  // a re-apply that surfaces a different variant set. Re-applying with the same
  // variants (e.g. only the region changed) keeps the current narrowing.
  useEffect(() => {
    setSelected(allVariants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantsKey]);

  const patchDraft = patch => setDraft(prev => ({ ...prev, ...patch }));
  const handleApply = () => setApplied(draft);
  const handleReset = () => {
    const defaults = defaultDraft();
    setDraft(defaults);
    setApplied(defaults);
  };
  const hasUnappliedChanges = JSON.stringify(draft) !== JSON.stringify(applied);

  // Table + chart rows: variants that are both returned and currently selected.
  const metricsByVariant = {};
  asRows(data).forEach(row => {
    metricsByVariant[row.variant] = metricFields(row);
  });
  const shownVariants = allVariants.filter(variant => selected.includes(variant));
  const variantRows = shownVariants
    .map(variant => ({
      label: variant,
      ...(metricsByVariant[variant] || metricFields({})),
    }))
    // Order by QL desc to match the table's default sort, so the side-by-side
    // chart and table read in the same order out of the box.
    .sort((a, b) => (b[METRICS.QL] || 0) - (a[METRICS.QL] || 0));

  // Grouped bar chart: x-axis = variant, one series per funnel metric, so
  // variants sit side by side for direct comparison (a vs b vs h).
  const chartDatasets = METRIC_KEYS.map(metric => ({
    label: METRIC_LABELS[metric],
    color: METRIC_COLORS[metric],
    data: variantRows.map(row => row[metric] || 0),
  }));

  const isFetching = hasExperiment && isProcessing;

  return (
    <>
      {isFetching && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SectionLabel>
        Analyze an experiment — every variant&apos;s funnel over the selected date range,
        optionally scoped to a single page
      </SectionLabel>
      <FiltersBar>
        <FilterField $width="250px">
          <FieldLabelText variant="eyebrow2">Date range</FieldLabelText>
          <RangePicker
            start={draft.start}
            end={draft.end}
            minDate={ANALYSIS_MIN_DATE}
            maxDate={today()}
            onChange={(start, end) => patchDraft({ start, end })}
          />
        </FilterField>
        <FilterField $width="280px">
          <FieldLabelText variant="eyebrow2">Experiment name</FieldLabelText>
          <Creatable
            isClearable
            value={draft.experiment ? { label: draft.experiment, value: draft.experiment } : null}
            onChange={option => patchDraft({ experiment: option ? option.value : '' })}
            options={experimentOptions}
            placeholder="Select or type an experiment"
            formatCreateLabel={input => `Use "${input}"`}
            noOptionsMessage={() => 'Type an experiment name and press Enter'}
            styles={{ menuPortal: base => ({ ...base, zIndex: 5 }) }}
          />
          <Text variant="caption" color={UI.muted}>
            Ongoing experiments listed; type any name for a closed/test one
          </Text>
        </FilterField>
        <FilterField $width="230px">
          <FieldLabelText variant="eyebrow2">Page path (optional)</FieldLabelText>
          <TextInput
            value={draft.page}
            onChange={value => patchDraft({ page: value })}
            placeholder="All pages — e.g. /pricing/"
          />
          <Text variant="caption" color={UI.muted}>
            Scope each variant to leads on one page (signup or landing)
          </Text>
        </FilterField>
        <FilterField $width="auto">
          <ChipGroupsRow>
            <ChipGroup
              label="Region"
              options={REGION_OPTIONS}
              value={draft.regions}
              onChange={regions => patchDraft({ regions })}
              multi
            />
            <ChipGroup
              label="User type"
              options={USER_TYPE_OPTIONS}
              value={draft.userTypes}
              onChange={userTypes => patchDraft({ userTypes })}
              multi
            />
          </ChipGroupsRow>
        </FilterField>
        {!!draft.regions.length && (
          <CountrySelect
            regions={draft.regions}
            value={draft.countries}
            onChange={countries => patchDraft({ countries })}
          />
        )}
        <ActionsField>
          <Button
            size="small"
            label={hasUnappliedChanges ? 'Apply *' : 'Apply'}
            onClick={handleApply}
          />
          <Button size="small" variant="tertiaryWithUnderline" label="Reset" onClick={handleReset} />
        </ActionsField>
      </FiltersBar>

      {!hasExperiment ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>
            Enter an experiment name (e.g. homepageImprovements) and press Apply
          </Text>
        </StateBlock>
      ) : error ? (
        <StateBlock>
          <Text variant="body1" color={UI.down}>{error}</Text>
        </StateBlock>
      ) : (isProcessing || !fresh) && !allVariants.length ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>Loading experiment…</Text>
        </StateBlock>
      ) : !allVariants.length ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>
            {`No variants found for "${applied.experiment.trim()}"${scopeSuffix} in this range — check the experiment key${
              pageScope ? ' or the page path' : ''
            }.`}
          </Text>
        </StateBlock>
      ) : (
        <>
          <SectionLabel>
            {`"${applied.experiment.trim()}" — ${allVariants.length} variant${
              allVariants.length === 1 ? '' : 's'
            }${scopeSuffix} in range`}
          </SectionLabel>
          <FiltersBar>
            <FilterField $width="260px">
              <FieldLabelText variant="eyebrow2">Compare variants</FieldLabelText>
              <MultiSelectApply
                label="Variants"
                options={allVariants}
                value={selected}
                onApply={setSelected}
                allLabel="All variants"
                optionLabel={variant => `Variant ${variant}`}
              />
              <Text variant="caption" color={UI.muted}>
                Pick which variants to show — e.g. just a and b, or b and h
              </Text>
            </FilterField>
          </FiltersBar>

          <SectionLabel>Variant breakdown — rows sum to the totals below</SectionLabel>
          <FunnelTable
            data={variantRows}
            columns={buildContributorColumns('Experiment value (variant)')}
            loading={isProcessing}
            defaultSorted={[{ id: METRICS.QL, desc: true }]}
            emptyMessage="No variants selected — pick some in the dropdown above"
            showTotals
          />

          <SectionLabel>Variant comparison — funnel counts side by side</SectionLabel>
          <Card>
            <ChartBox $height={340}>
              {variantRows.length ? (
                <FunnelBarChart
                  labels={variantRows.map(row => `Variant ${row.label}`)}
                  datasets={chartDatasets}
                  stacked={false}
                  showLegend
                />
              ) : (
                <Text variant="body3" color={UI.muted}>
                  No variants selected — pick some in the dropdown above
                </Text>
              )}
            </ChartBox>
            <Separator height={6} transparent />
            <Text variant="caption" color={UI.muted}>
              QL / Trials Scheduled / Trials Done / Enrolled for each selected variant.
            </Text>
          </Card>
        </>
      )}
    </>
  );
});

export default ExperimentAnalysis;
